import { Button, Dialog } from '@/components/UI'

export type LotteryResultItem = {
  /** userRewardTransactionId */
  id: number
  /** 'win': 当選, 'lose': ハズレ */
  result: 'win' | 'lose'
  /** 獲得試行日時（フォーマット済み文字列） */
  date: string
  /**
   * ダイアログ内ボタンに表示するラベル文言。
   * prize.type に応じて detail.tsx 側で生成して渡す（Table B 参照）。
   * 未指定の場合はデフォルト「賞品を受け取る」を表示。
   */
  label?: string
  /**
   * 「賞品を受け取る」ボタン押下時のコールバック。
   * 未指定の場合はボタンが非活性（disabled）になる。
   * isLotteryLost=true の場合は渡さない。
   */
  onReceive?: () => void
}

type Props = {
  opened: boolean
  onClose: () => void
  results?: LotteryResultItem[]
  /** 'lottery': 抽選結果一覧、'acquired': 獲得賞品一覧 */
  mode?: 'lottery' | 'acquired'
}

/**
 * 抽選結果 / 獲得賞品一覧ダイアログ。
 * Figma: node 439-5708 / 439-5939
 *
 * mode='lottery':
 *   - タイトル「抽選結果一覧」
 *   - 当選行: 日付 + 黄色ボタン「賞品を受け取る」（onReceive があれば活性）
 *   - ハズレ行: 日付 + グレースパン「はずれ」
 *
 * mode='acquired':
 *   - タイトル「獲得賞品一覧」
 *   - 当選行（result=win）: 日付 + ボタン（onReceive があれば活性・黄色、なければ非活性・グレー）
 *   - ハズレ行（result=lose）: 日付 + グレー非活性スパン「はずれ」（mode に関わらず共通）
 */
export function DialogV2LotteryResultList({ opened, onClose, results = [], mode = 'lottery' }: Props) {
  const title = mode === 'lottery' ? '抽選結果一覧' : '獲得賞品一覧'
  const emptyMessage = mode === 'lottery' ? '抽選結果がありません' : '獲得履歴がありません'

  return (
    <Dialog selectorState={opened} closeFunc={onClose} title={title}>
      <div className="flex flex-col gap-0">
        {results.length === 0 ? (
          <p className="py-4 text-center text-[12px] text-[#cbcbce]">{emptyMessage}</p>
        ) : (
          <ul className="divide-y divide-[#f0f0f0]">
            {results.map((item) => {
              const isWin = item.result === 'win'
              const isActive = isWin && !!item.onReceive
              return (
                <li key={item.id} className="flex items-center justify-between gap-3 py-[10px]">
                  {/* 日付 */}
                  <p className="text-[12px] leading-[16px] text-black">{item.date}</p>
                  {/* ボタン */}
                  {isWin ? (
                    <Button
                      label={item.label ?? '獲得する'}
                      variant="primary"
                      size="xs"
                      disabled={!isActive}
                      onClick={isActive ? item.onReceive : undefined}
                      className="shrink-0"
                    />
                  ) : (
                    <span className="flex h-[32px] shrink-0 items-center rounded-[8px] bg-[#eeeeee] px-[16px] text-[13px] font-bold leading-[16px] text-[#757575]">
                      はずれ
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        {/* OK（閉じる）ボタン */}
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={onClose}
            className="h-[48px] w-full rounded-[32px] bg-[#eeeeee] text-[15px] font-bold leading-[24px] text-[#757575]"
          >
            OK
          </button>
        </div>
      </div>
    </Dialog>
  )
}
